
import { getApiBaseUrl } from "./configHelper";

const OPEN_API_ENDPOINTS = [
  "Login/LoginUser",
  "Login/RefreshToken",
  "Login/Companydetails",
  "Login/RequestResetPassword",
  "Login/ConfirmResetPassword",
  "Login/ForceSignOutVerifyOtp",
];

const isOpenEndpoint = (endpoint = "") =>
  OPEN_API_ENDPOINTS.some((openEndpoint) =>
    endpoint.toLowerCase().startsWith(openEndpoint.toLowerCase())
  );

const redirectToSessionExpired = (message) => {
  if (typeof window === "undefined") return;

  const publicPaths = ["/", "/login", "/forcelogin", "/session-expired"];
  if (publicPaths.includes(window.location.pathname)) return;

  if (message) {
    sessionStorage.setItem("sessionExpiredMessage", message);
  }

  window.location.replace("/session-expired");
};

// Generate Bearer auth header for protected APIs only
const getAuthHeader = (endpoint) => {
  if (isOpenEndpoint(endpoint)) {
    return {};
  }

  const accessToken = sessionStorage.getItem("AccessToken");

  if (!accessToken) {
    const message = "Your session has expired. Please login again.";
    redirectToSessionExpired(message);
    throw new Error(message);
  }

  return { Authorization: `Bearer ${accessToken}` };
};

const clearAuthStorage = () => {
  if (typeof window === "undefined") return;

  ["AccessToken", "RefreshToken", "CurrentAuthToken", "AuthToken", "userId", "userName", "UserInfo", "LogId"].forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
};

const persistTokens = ({ accessToken, refreshToken } = {}) => {
  if (accessToken) {
    sessionStorage.setItem("AccessToken", accessToken);
  }

  if (refreshToken) {
    sessionStorage.setItem("RefreshToken", refreshToken);
  }
};

const getStoredRefreshToken = () =>
  sessionStorage.getItem("RefreshToken");

let refreshPromise = null;

const refreshAccessToken = async (baseUrl) => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    throw new Error("Your session has expired. Please login again.");
  }

  const params = getDbParams();
  const url = new URL(`${baseUrl}/Login/RefreshToken`);
  Object.entries(params)
    .filter(([_, val]) => val !== undefined && val !== null && val !== "")
    .forEach(([key, val]) => url.searchParams.append(key, val));

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    throw new Error("Your session has expired. Please login again.");
  }

  const json = await response.json();
  const data = unwrapResponse(json);
  const accessToken = data?.accessToken;
  const nextRefreshToken = data?.refreshToken;

  if (!accessToken || !nextRefreshToken) {
    throw new Error("Your session has expired. Please login again.");
  }

  persistTokens({ accessToken, refreshToken: nextRefreshToken });
  return data;
};

const getRefreshPromise = (baseUrl) => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken(baseUrl).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};


// 🧩 Retrieve DB parameters from session
const getDbParams = (extraParams = {}) => {
  const sessionDatabaseKey = sessionStorage.getItem("databaseKey");
  const resolvedDatabaseKey = (extraParams.databaseKey ?? sessionDatabaseKey) || sessionDatabaseKey;

  if (!resolvedDatabaseKey) {
    console.warn("⚠️ Missing DB parameters in sessionStorage or extraParams");
  }

  return { ...extraParams, databaseKey: resolvedDatabaseKey };
};

// 🧱 Centralized error handler
const handleApiError = async (response, { endpoint, baseUrl, retryRequest, hasRetried = false } = {}) => {
 if (response.status === 401) {
   const data = await response.json().catch(() => ({}));
   const errorMessage =
     data?.message ||
     data?.title ||
     data?.error ||
     "Your session has expired. Please login again.";
   console.error("401 API Error:", errorMessage);

   if (!hasRetried && !isOpenEndpoint(endpoint)) {
     try {
       await getRefreshPromise(baseUrl);
       return await retryRequest();
     } catch (refreshError) {
       console.error("Refresh token failed:", refreshError);
     }
   }

   clearAuthStorage();
   redirectToSessionExpired(errorMessage);
   throw new Error(errorMessage);
 }

 let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
 try {
   const data = await response.json();
   errorMessage = data?.message || data?.error || errorMessage;
 } catch {
   /* ignore non-JSON errors */
 }
 throw new Error(errorMessage);
};

// 🧠 Helper to unwrap single-key JSON objects (like {checkLogin: {...}})
const unwrapResponse = (json) => {
 if (typeof json === "object" && Object.keys(json).length === 1) {
   return json[Object.keys(json)[0]];
 }
 return json;
};

// =======================================================
// 🧩 API Core Methods
// =======================================================
const api = {
 /**
  * 🔹 GET Request
  * @param {string} endpoint - API endpoint
  * @param {object} params - Query parameters
  */
 get: async (endpoint, params = {}, baseUrl, options = {}) => {
   if (!baseUrl) baseUrl = await getApiBaseUrl();
   const url = new URL(`${baseUrl}/${endpoint}`);
   Object.entries(params)
     .filter(([_, val]) => val !== undefined && val !== null && val !== "")
     .forEach(([key, val]) => url.searchParams.append(key, val));

   const response = await fetch(url, {
     method: "GET",
     headers: {
       "Accept": "application/json",
       ...getAuthHeader(endpoint),
     },
   });

   if (!response.ok) {
     const retryResult = await handleApiError(response, {
       endpoint,
       baseUrl,
       hasRetried: options.hasRetried,
       retryRequest: () => api.get(endpoint, params, baseUrl, { hasRetried: true }),
     });
     if (retryResult !== undefined) return retryResult;
   }
   const json = await response.json();
   return unwrapResponse(json);
 },

 /**
  * 🔹 POST Request
  * @param {string} endpoint - API endpoint
  * @param {object} body - Request body
  * @param {object} params - Optional query parameters
  */
 post: async (endpoint, body = {}, params = {}, baseUrl, options = {}) => {
   if (!baseUrl) baseUrl = await getApiBaseUrl();
   const url = new URL(`${baseUrl}/${endpoint}`);
   Object.entries(params)
     .filter(([_, val]) => val !== undefined && val !== null && val !== "")
     .forEach(([key, val]) => url.searchParams.append(key, val));

   const response = await fetch(url, {
     method: "POST",
     headers: {
       "Content-Type": "application/json",
       "Accept": "application/json",
       ...getAuthHeader(endpoint),
     },
     body: JSON.stringify(body),
   });

   if (!response.ok) {
     const retryResult = await handleApiError(response, {
       endpoint,
       baseUrl,
       hasRetried: options.hasRetried,
       retryRequest: () => api.post(endpoint, body, params, baseUrl, { hasRetried: true }),
     });
     if (retryResult !== undefined) return retryResult;
   }
   const json = await response.json();
   return unwrapResponse(json);
 },

 // =======================================================
 // 🚀 ERP API Methods
 // =======================================================

 Companydetails: async () => {
   const params = getDbParams();
   return await api.get("Login/Companydetails", params);
 },

 LoginUser: async (userName, userpwd, forceSignout = 0) => {
   const params = getDbParams();
   return await api.post("Login/LoginUser", { userName, userpwd, forceSignout }, params);
 },

 RefreshToken: async (refreshToken) => {
   const params = getDbParams();
   return await api.post("Login/RefreshToken", { refreshToken }, params);
 },

 GetUserNameByUsrId: async (UsrId) => {
   const params = getDbParams({ UsrId });
   return await api.get("Login/GetUserNameByUsrId", params);
 },

 GetMdlIdBySeqId: async (SeqId) => {
   const params = getDbParams({ SeqId });
   return await api.get("Login/GetMdlIdBySeqId", params);
 },

 GetModuleByMdlId: async (MdlId) => {
   const params = getDbParams({ MdlId });
   return await api.get("Login/GetModuleByMdlId", params);
 },

 GetMenuTypes: async (UserId) => {
   const params = getDbParams({ UserId });
   return await api.get("Menu/GetMenuTypes", params);
 },
 
  GetSubMenuList: async (UserId,Menuname) => {
   const params = getDbParams({UserId, Menuname });
   return await api.get("Menu/GetSubMenuList", params);
 },
  GetAllSubMenuList: async (UserId) => {
    const params = getDbParams({UserId });
    return await api.get("Menu/GetAllSubMenuList", params);
  },

  //ramya

  GetUserTaskList: async (userName) => {
  const params = getDbParams({ userName });
  return await api.get("Menu/GetUserTaskList", params);
},

MarkTaskAsRead: async (payload) => {
  const params = getDbParams();
  return await api.post("Menu/MarkTaskAsRead", payload, params);
},

SaveUpdateTaskLog: async (payload) => {
  const params = getDbParams();
  return await api.post("Menu/SaveUpdateTaskLog", payload, params);
},

DeleteTaskLog: async (tlogId) => {
  const params = getDbParams({ tlogId });
  return await api.post("Menu/DeleteTaskLog", {}, params);
},

GetApprovalCount: async (usrId) => {
  const params = getDbParams({ usrId });
  return await api.get("Menu/GetApprovalCount", params);
},

GetMailOutbox: async (userId, userName) => {
  const params = getDbParams({ userId, usrId: userId, userName });
  return await api.get("Menu/GetMailOutbox", params);
},

GetVendorToAddress: async (logId) => {
  const params = getDbParams({ logId });
  return await api.get("Menu/GetVendorToAddress", params);
},

DeleteMailOutBox: async (logId) => {
  const params = getDbParams({ logId });
  return await api.post("Menu/DeleteMailOutBox", {}, params);
},

SendMailByLogId: async (logId) => {
  const params = getDbParams({ logId });
  return await api.post("Menu/SendMailByLogId", {}, params);
},

UpdateMailContent: async (logId, mailContent, toEMailid) => {
  const params = getDbParams({ logId });
  return await api.post("Menu/UpdateMailContent", { mailContent, toEMailid }, params);
},
//----


 LogoutUser: async (logId, userId,currentAuthToken) => {
  const params = getDbParams();
  const resolvedCurrentAuthToken =
    currentAuthToken ||
    sessionStorage.getItem("CurrentAuthToken");
  return await api.post("Login/LogoutUser", { logId, userId,currentAuthToken: resolvedCurrentAuthToken }, params);
},

  ResetPassword: async (userName, emailId, newPassword) => {
   const params = getDbParams();
   return await api.post("Login/ResetPassword", { userName, emailId, newPassword }, params);
 },
  //------------------Check Authorized Login-------------//

   AuthTokenActiveCheck: async (payload) => {
   const params = getDbParams();
   return await api.post("Login/AuthTokenActiveCheck", payload, params);
 },
 //------------------Force SignOut-------------//

   ForceSignOutUser: async (payload) => {
   const params = getDbParams();
   return await api.post("Login/ForceSignOutUser", payload, params);
 },

  //------------------Request reset password-------------//

   RequestResetPassword: async (payload) => {
   const params = getDbParams();
   return await api.post("Login/RequestResetPassword", payload, params);
 },

   //------------------Update reset password-------------//

   ConfirmResetPassword: async (payload) => {
   const params = getDbParams();
   return await api.post("Login/ConfirmResetPassword", payload, params);
 },


 //------------------Verify Otp -------------//

   ForceSignOutVerifyOtp: async (UserName,AuthCode) => {
    const params = getDbParams({UserName,AuthCode});
    return await api.get("Login/ForceSignOutVerifyOtp", params);
  },
 //------------------Advance Reports-------------//

 AdvRptGetReportList: async (User) => {
    const params = getDbParams({User});
    return await api.get("AdvanceRmpRpt/GetAdvreportList", params);
  },

 AdvRptGetUserControls: async (UserName,Rptid,SessId) => {
    const params = getDbParams({UserName,Rptid,SessId});
    return await api.get("AdvanceRmpRpt/GetAdvReportControls", params);
  },

  AdvRptCreateCallId: async (payload) => {
   const params = getDbParams();
   return await api.post("AdvanceRmpRpt/AdvRptGenerate", payload, params);
 },
 //------------------GLOBAL SEARCH-------------//

  GetSearchAndLog: async (keyword, userName, useDateFilter, mdlIds, fromDate, toDate, databaseKey) => {
    const params = getDbParams({ keyword, userName, useDateFilter, mdlIds, fromDate, toDate, databaseKey });
    return await api.get("SearchAndLogApi_016/GetSearchAndLog", params);
  },
}


export default api;
